import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import { TextFieldProps } from '@mui/material/TextField';

export type AutocompleteOption = {
  id: string;
  name: string;
};

export type AutoCompleteProps<T extends AutocompleteOption> = {
  data: AutocompleteOption[];
  label: string;
  setValue: React.Dispatch<React.SetStateAction<T | undefined>>;
};

const AutoComplete = <T extends AutocompleteOption>({ data, label, setValue }: AutoCompleteProps<T>) => {
  const { t } = useTranslation(['random']);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<readonly T[]>([]);
  const loading = open && options.length === 0;

  React.useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    (() => {
      if (active && data) {
        const notReadOnly = [...data];
        setOptions(notReadOnly.sort((a, b) => a.name.localeCompare(b.name)) as T[]);
      }
    })();

    return () => {
      active = false;
    };
  }, [data, loading]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  return (
    <Autocomplete
      id={`asynchronous-${label}`}
      open={open}
      loadingText={t('loadingText')}
      noOptionsText={t('noOptionsText')}
      fullWidth
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      onChange={(event: unknown, newValue: T | null) => {
        setValue(newValue ? (newValue as T) : undefined);
      }}
      isOptionEqualToValue={(option: AutocompleteOption, value: AutocompleteOption) => option.name === value.name}
      getOptionLabel={(option: AutocompleteOption) => `${option.name}`}
      options={options}
      autoHighlight
      loading={loading}
      getOptionKey={(option: AutocompleteOption) => option.id}
      key={'id'}
      renderInput={(params: TextFieldProps) => (
        <TextField
          {...params}
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps?.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  ) as React.ReactNode;
};

export { AutoComplete };
export default AutoComplete;
